import React, { JSX, useState } from 'react'
import { EmptyDataCard } from 'components/EmptyDataCard'
import classNames from 'classnames'
import { cleanVariables, DaySales, getRatioClass, graphqlRequest, transformSalesDataToLocalTime } from 'lib/helpers'
import { AnalyticsMetafield, TimeSeriesTotals } from 'types/api'
import { useQuery } from '@tanstack/react-query'
import getHourTimeseries from '../graphql/getHourTimeseries.graphql'
import { RefreshingBlock } from './Loading'

export interface HourlyTableProps {
  client: AnalyticsMetafield | null
  token?: string
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
}
interface Hour {
  ratio: number
  label: string
}

interface HourCellProps {
  hour: Hour
  showTooltip: boolean
  onClick: () => void
  onBlur: () => void
}

const HOUR_NAMES = [
  '8am-12pm',
  '12pm-4pm',
  '4pm-8pm',
  '8pm-12am',
  '12am-4am',
  '4am-8am'
]

const oneHourInSeconds = 60 * 60

const HourCell = ({ hour, showTooltip, onClick, onBlur }: HourCellProps): JSX.Element => {
  const tooltipClasses = classNames(
    'absolute left-1/2 -top-8 -translate-x-1/2 transition-all duration-200 bg-lm-text-secondary text-gray-100 dark:dm-text-secondary dark:text-gray-100  text-xs px-2 py-1 rounded shadow z-10 whitespace-nowrap pointer-events-none',
    {
      'scale-100 opacity-100': showTooltip,
      'group-hover:scale-100 group-hover:opacity-100': true,
      'scale-0 opacity-0': !showTooltip
    }
  )

  return (
    <button
      data-testid='tooltip-button'
      className='relative group h-[30px] mx-1 my-[0.2rem] text-[10px] touch-manipulation'
      onClick={onClick}
      onBlur={onBlur}
      tabIndex={0}
    >
      <span className={`block w-full h-full rounded-sm ${getRatioClass(hour.ratio)}`} />
      <div className={tooltipClasses}>{hour.label}</div>
    </button>
  )
}
const HourCells = ({ data }: { data: DaySales[] }): JSX.Element => {
  const [tooltipState, setTooltipState] = useState<Record<string, boolean>>({})

  const toggleTooltip = (key: string): void => {
    setTooltipState(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const hideTooltip = (key: string): void => {
    setTooltipState(prev => ({ ...prev, [key]: false }))
  }

  const renderDayColumn = (day: DaySales): JSX.Element => {
    return (
      <div key={`day-${day.name}`} className='flex flex-col flex-grow basis-0'>
        {HOUR_NAMES.map((slotLabel, index) => {
          const hour = day.hours[slotLabel]
          const key = `${day.name}-${index}`

          return (
            <HourCell
              key={key}
              hour={hour}
              showTooltip={!!tooltipState[key]}
              onClick={() => toggleTooltip(key)}
              onBlur={() => hideTooltip(key)}
            />
          )
        })}
        <div className='h-[30px] my-[0.2rem] text-[10px]'>{day.name}</div>
      </div>
    )
  }

  const renderHourLabels = (): JSX.Element => (
    <div className='flex flex-col flex-grow basis-0'>
      {HOUR_NAMES.map((label) => (
        <div
          key={`hourName-${label}`}
          className='h-[30px] my-[0.2rem] text-[10px] whitespace-nowrap'
        >
          {label}
        </div>
      ))}
    </div>
  )

  return (
    <div className='flex flex-row text-center uppercase'>
      {data.map(renderDayColumn)}
      {renderHourLabels()}
    </div>
  )
}

export const HourlyTable = ({ filters, client, token }: HourlyTableProps): JSX.Element => {
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'hourTimeSeries', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate,
        binSize: oneHourInSeconds
      })
      const { timeSeriesTotals } = await graphqlRequest<{ timeSeriesTotals: TimeSeriesTotals[] }>(
        getHourTimeseries, cleanVar, token
      )

      const hourlyDataRaw = timeSeriesTotals.length > 0 ? transformSalesDataToLocalTime(timeSeriesTotals) : []

      return hourlyDataRaw
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  if (!isFetching && data?.length === 0) {
    return <EmptyDataCard message='No data available for specified filters' />
  }

  if (isFetching) {
    return (
      <div className='relative'>
        {RefreshingBlock}
        <EmptyDataCard message='No data available for specified filters' />
      </div>
    )
  }

  return (
    <div className='w-full relative'>
      <div className='flex items-center justify-between px-4 pt-2 pb-3'>
        <h2 className='text-sm font-medium'>Sales by time of day</h2>
      </div>
      <HourCells data={data ?? []} />
    </div>
  )
}
